import get from "lodash/get";
import { Division, GetScore, Lifter, Meet } from "types";
import { toKg } from "util/conversions";
import { getRealAge, getTotal } from "util/lifterHelper";
import { isLbsMeet } from "util/meetHelper";
import { competitionCode } from "util/standardDivisions";
import { ageCoef } from "./age";

const equipmentLevelConversionMap = {
  RAW: "RAW",
  CLASSIC_RAW: "RAW",
  RAW_WITH_WRAPS: "RAW",
  RAW_CLASSIC: "RAW",
  RAW_MODERN: "RAW",
  RAW_AMATEUR: "RAW",
  RAW_PRO: "RAW",
  EQUIPPED: "EQUIPPED",
  SINGLE_PLY: "EQUIPPED",
  MULTI_PLY: "EQUIPPED",
  UNLIMITED: "EQUIPPED",
  MULTIPLY: "EQUIPPED",
  EQUIPPED_AMATEUR: "EQUIPPED",
  EQUIPPED_PRO: "EQUIPPED",
};

export const ipfFormula = function (
  total: number,
  division: Division | undefined,
  lifter: Lifter,
  meet: Meet
) {
  // Lifters under 14 years of age technically are not valid for this forumla, but this check just confuses people.
  if (meet.federation === "CPU") {
    const lifterAge = getRealAge(lifter, meet);
    if (lifterAge && lifterAge < 14) {
      return 0;
    }
  }

  if (total <= 0 || !division) {
    return 0;
  }

  const parameters = {
    MALE: {
      RAW: {
        PL: { c1: 1199.72839, c2: 1025.18162, c3: 0.00921 },
        BP: { c1: 320.98041, c2: 281.40258, c3: 0.01008 },
      },
      EQUIPPED: {
        PL: { c1: 1236.25115, c2: 1449.21864, c3: 0.01644 },
        BP: { c1: 381.22073, c2: 733.79378, c3: 0.02398 },
      },
    },
    FEMALE: {
      RAW: {
        PL: { c1: 610.32796, c2: 1045.59282, c3: 0.03048 },
        BP: { c1: 142.40398, c2: 442.52671, c3: 0.04724 },
      },
      EQUIPPED: {
        PL: { c1: 758.63878, c2: 949.31382, c3: 0.02435 },
        BP: { c1: 221.82209, c2: 357.00377, c3: 0.02937 },
      },
    },
  };

  const compCode = competitionCode(division);

  let bodyWeight = lifter.bodyWeight;

  if (!bodyWeight) {
    return 0;
  }

  if (!lifter.gender) {
    return 0;
  }

  if (isLbsMeet(meet)) {
    bodyWeight = toKg(bodyWeight);
  }

  const equipmentLevel = equipmentLevelConversionMap[division.rawOrEquipped];
  if (!equipmentLevel) {
    return 0;
  }

  const divisionParameters = get(parameters, [
    lifter.gender,
    equipmentLevel,
    compCode,
  ]);
  if (!divisionParameters) {
    return 0;
  }

  const c1 = divisionParameters.c1;
  const c2 = divisionParameters.c2;
  const c3 = divisionParameters.c3;

  return Math.max(
    0,
    (total * 100.0) / (c1 - c2 * Math.exp(-1.0 * c3 * bodyWeight))
  );
};

export const getIpfPoints: GetScore = ({
  lifter,
  division,
  meet,
  forecasted,
}) => {
  let total = getTotal({ lifter, division, meet, forecasted });
  if (isLbsMeet(meet)) {
    total = toKg(total);
  }

  return ipfFormula(total, division, lifter, meet);
};

export const getIpfAndAgePoints: GetScore = ({
  lifter,
  division,
  meet,
  forecasted,
}) => {
  return (
    getIpfPoints({ lifter, division, meet, forecasted }) * ageCoef(lifter, meet)
  );
};
